import { useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useProjectDetails, useUpdateProject } from "@/api/api";
import { useUpdateCharacterV2, useUpdateLocationV2, useUpdateShotV2 } from "@/api/entityOpsV2";
import { useStore } from "@/contexts/StoreContext";
import type { ProjectTransactionNotification } from "@/types/api";
import type { ProjectDetailsCharacter, ProjectDetailsLocation, ProjectDetailsStoryboardShot } from "@/types/project";
import {
  applyCompletedGenerationTransaction,
  isCompletedMediaGenerationTransaction,
  oldestRequestedFirst
} from "@/services/applyCompletedGenerationTransaction";
import { pickNewerProjectDetails } from "@/services/projectDetails";

function completedMediaGenerationKey(items: readonly ProjectTransactionNotification[]): string {
  return items
    .filter((item) => isCompletedMediaGenerationTransaction(item))
    .map((item) => item.transaction_id)
    .toSorted()
    .join("\0");
}

const useApplyCompletedGenerationTransactions = (
  projectId: string | undefined,
  items: readonly ProjectTransactionNotification[]
): void => {
  const handledRef = useRef<Set<string>>(new Set());
  const applyingRef = useRef(false);
  const { actionStore } = useStore();
  const queryClient = useQueryClient();
  const updateProjectMutation = useUpdateProject();
  const updateCharacterV2Mutation = useUpdateCharacterV2<ProjectDetailsCharacter>();
  const updateLocationV2Mutation = useUpdateLocationV2<ProjectDetailsLocation>();
  const updateShotV2Mutation = useUpdateShotV2<ProjectDetailsStoryboardShot>();
  const projectDetailsQuery = useProjectDetails(projectId ?? null);

  const projectDetailsDataRef = useRef(projectDetailsQuery.data);
  projectDetailsDataRef.current = projectDetailsQuery.data;

  const storeProjectDetailsRef = useRef(actionStore.projectDetails);
  storeProjectDetailsRef.current = actionStore.projectDetails;

  const refetchProjectDetailsRef = useRef(projectDetailsQuery.refetch);
  refetchProjectDetailsRef.current = projectDetailsQuery.refetch;

  const completedItemsKey = useMemo(() => completedMediaGenerationKey(items), [items]);

  useEffect(() => {
    if (!projectId || completedItemsKey.length === 0) {
      return;
    }

    const toApply = oldestRequestedFirst(
      items.filter((item) => isCompletedMediaGenerationTransaction(item) && !handledRef.current.has(item.transaction_id))
    );
    if (toApply.length === 0 || applyingRef.current) {
      return;
    }

    let cancelled = false;
    applyingRef.current = true;

    void (async () => {
      try {
        let details = pickNewerProjectDetails(projectDetailsDataRef.current, storeProjectDetailsRef.current);
        if (!details?.project_id) {
          const refetched = await refetchProjectDetailsRef.current();
          if (cancelled) {
            return;
          }

          details = pickNewerProjectDetails(refetched.data, storeProjectDetailsRef.current);
        }

        if (!details?.project) {
          return;
        }

        for (const transaction of toApply) {
          if (cancelled || handledRef.current.has(transaction.transaction_id)) {
            continue;
          }

          try {
            // oxlint-disable-next-line no-await-in-loop -- sequential on purpose: each transaction needs the previous one's updated `details`
            const result = await applyCompletedGenerationTransaction({
              transaction,
              latestDetails: details,
              updateProject: updateProjectMutation.mutateAsync,
              saveCharacterV2: updateCharacterV2Mutation.mutateAsync,
              saveLocationV2: updateLocationV2Mutation.mutateAsync,
              saveShotV2: updateShotV2Mutation.mutateAsync,
              actionStore,
              queryClient
            });

            if (result !== null) {
              handledRef.current.add(transaction.transaction_id);
              details = result;
              storeProjectDetailsRef.current = result;
            }
          } catch {
            // Leave unhandled so the next notification refresh can retry.
          }
        }
      } finally {
        applyingRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    actionStore,
    completedItemsKey,
    items,
    projectId,
    queryClient,
    updateCharacterV2Mutation.mutateAsync,
    updateLocationV2Mutation.mutateAsync,
    updateShotV2Mutation.mutateAsync,
    updateProjectMutation.mutateAsync
  ]);
};

export default useApplyCompletedGenerationTransactions;
